import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IconButton, Popover, Table, TableBody, TableRow, TableCell, Typography, colors } from '@mui/material';
import { FC, MouseEvent, memo, useCallback, useState, useMemo } from 'react';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsNotAllocReason } from 'src/entities/PlanningsNotAllocReasons.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';

type Props = {
  filteredNotAllocReasons: PlanningsNotAllocReason[];
  notAllocDeliveries: PlanningsDeliveryEntity[];
  notAllocTrucks: PlanningsTruckEntity[];
  notAllocDrivers: PlanningsDriverEntity[];
};

export const NotAllocReasonPresenter: FC<Props> = memo(({
  filteredNotAllocReasons,
  notAllocDeliveries,
  notAllocTrucks,
  notAllocDrivers,
}) => {
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement>(null);

  const handlePopoverOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setPopoverAnchorEl(event.currentTarget);
  }, []);

  const handlePopoverClose = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setPopoverAnchorEl(null);
  }, []);

  const handlePopoverClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  const notAllocReasons = useMemo(() => {
    const processed: { truckId: number, driverId: number, reason: string }[] = [];
    return filteredNotAllocReasons.filter((reason) => {
      const delivery = notAllocDeliveries.find((it) => it.id === reason.delivery_id);
      const truck = delivery ? notAllocTrucks.find((it) => it.id === delivery.truckId) : null;
      const driver = delivery ? notAllocDrivers.find((it) => it.id === delivery.driverId) : null;

      if (processed.findIndex((it) => it.truckId === truck?.id && it.driverId === driver?.id && it.reason === reason.message) >= 0) {
        return false;
      }
      processed.push({ truckId: truck?.id, driverId: driver?.id, reason: reason.message });
      return true;
    });
  }, [filteredNotAllocReasons, notAllocDeliveries, notAllocDrivers, notAllocTrucks]);

  return (
    <>
      <IconButton
        onClick={handlePopoverOpen}
      >
        <InfoOutlinedIcon sx={{ color: colors.blue[900] }} />
      </IconButton>
      <Popover
        id={popoverAnchorEl ? 'not-alloc-reason-popover' : undefined}
        open={Boolean(popoverAnchorEl)}
        anchorEl={popoverAnchorEl}
        onClose={handlePopoverClose}
        onClick={handlePopoverClick}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Table>
          <TableBody>
            {notAllocReasons.map((reason, index) => {
              const delivery = notAllocDeliveries.find((it) => it.id === reason.delivery_id);
              const truck = delivery ? notAllocTrucks.find((it) => it.id === delivery.truckId) : null;
              const driver = delivery ? notAllocDrivers.find((it) => it.id === delivery.driverId) : null;

              return (
                <TableRow key={[delivery?.id, truck?.id, driver?.id, index].join('-')}>
                  <TableCell sx={{ p: 1 }}><Typography noWrap>{truck?.temporary ? '車両未設定' : truck?.licensePlateValue}</Typography></TableCell>
                  <TableCell sx={{ p: 1 }}><Typography noWrap>{driver?.temporary ? 'ドライバー未設定' : driver?.name}</Typography></TableCell>
                  <TableCell sx={{ p: 1 }}><Typography noWrap>{reason?.message}</Typography></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Popover>
    </>
  );
});
